import productConnectImg from "@/assets/product-connect.jpg"
import productGrowImg from "@/assets/product-grow.jpg"
import productSalesLinkImg from "@/assets/product-sales-link.jpg"
import productSellImg from "@/assets/product-sell.jpg"

export const PRODUCT_SLUGS = [
  "sales-link",
  "sell",
  "grow",
  "connect",
] as const

export type ProductSlug = (typeof PRODUCT_SLUGS)[number]

export type ProductFeatureHighlight = {
  title: string
  description: string
}

export type ProductContent = {
  slug: ProductSlug
  title: string
  tagline: string
  intro: string
  painPoints: string[]
  howSqaleshopHelps: string[]
  outcomes: string[]
  features: ProductFeatureHighlight[]
  image: string
}

export const products: Record<ProductSlug, ProductContent> = {
  "sales-link": {
    slug: "sales-link",
    title: "Your sales link",
    tagline: "One link. Your brand. Everywhere you sell.",
    intro:
      "Your Sqaleshop link is the home for your catalog, checkout, and brand, the single URL you paste in your bio, stories, WhatsApp status, and anywhere else customers find you.",
    painPoints: [
      "You send different product photos and prices in every chat.",
      "Your bio link is a messy tree of random pages, not a real shop.",
      "Updating how you look means rebuilding links from scratch.",
    ],
    howSqaleshopHelps: [
      "Claim sqaleshop.com/yourbrand and share one link everywhere.",
      "Customize colors, logo, and layout so the page feels like you.",
      "Paste the same URL in Instagram, WhatsApp, TikTok, and campaigns.",
    ],
    outcomes: [
      "Customers always land on the same professional storefront.",
      "You stop rebuilding offers every time someone asks for the menu.",
      "Your brand shows up consistently wherever you sell.",
    ],
    features: [
      {
        title: "Sales page",
        description: "sqaleshop.com/yourbrand, share one link everywhere",
      },
      {
        title: "Custom appearance",
        description: "Brand colors, logo, and layout that feel like you",
      },
      {
        title: "Share anywhere",
        description: "Bio, stories, WhatsApp, paste your link and sell",
      },
    ],
    image: productSalesLinkImg,
  },
  sell: {
    slug: "sell",
    title: "Sell & checkout",
    tagline: "Products, payments, and orders in one flow",
    intro:
      "Stop negotiating every sale in DMs. List products with variants, take orders through a clean checkout, and manage everything from a single dashboard instead of scrolling chat history.",
    painPoints: [
      "Sizes, colors, and availability get lost in long message threads.",
      "Payment details are scattered across screenshots and voice notes.",
      "You have no single view of what sold today versus what is still pending.",
    ],
    howSqaleshopHelps: [
      "Add photos, prices, sizes, and availability so shoppers self-serve.",
      "Collect order details and payment in one structured checkout.",
      "See every order in a dashboard, not buried in chat.",
    ],
    outcomes: [
      "Fewer “is this still available?” messages.",
      "Clearer fulfillment from paid order to delivery.",
      "Less time copying order details into notes and spreadsheets.",
    ],
    features: [
      {
        title: "Products & variants",
        description: "Photos, prices, sizes, and availability",
      },
      {
        title: "Checkout & payments",
        description: "Order details and payment in one flow",
      },
      {
        title: "Order dashboard",
        description: "Every order in one place, not buried in chat",
      },
    ],
    image: productSellImg,
  },
  grow: {
    slug: "grow",
    title: "Grow with insight",
    tagline: "See what sells, and what to do next",
    intro:
      "Guessing from DMs only goes so far. Sqaleshop shows visits, product views, orders, and revenue in one place, with customer insights and AI recommendations when you are ready to scale.",
    painPoints: [
      "You know you are busy, but not which products actually drive revenue.",
      "Repeat buyers are invisible until they message again.",
      "You upgrade tools only when chaos forces it, then feel behind.",
    ],
    howSqaleshopHelps: [
      "Track visits → product views → orders → revenue in one analytics view.",
      "See who bought what, how often, and total spent.",
      "Use Sqaleshop AI recommendations inside your analytics tab on Growth.",
    ],
    outcomes: [
      "Clearer decisions on what to restock, promote, or drop.",
      "A real picture of customer value beyond chat volume.",
      "Insight that grows with you when you unlock Growth.",
    ],
    features: [
      {
        title: "Sales analytics",
        description: "Visits → product views → orders → revenue",
      },
      {
        title: "Customer insights",
        description: "Who bought what, how often, total spent",
      },
      {
        title: "Sqaleshop AI",
        description: "Smart recommendations in your analytics tab",
      },
    ],
    image: productGrowImg,
  },
  connect: {
    slug: "connect",
    title: "Stay connected",
    tagline: "WhatsApp when they need you, structure when they don’t",
    intro:
      "Buyers still want to talk. Sqaleshop keeps WhatsApp in the flow, builds a customer database from every order, and helps you reach abandoned carts and repeat buyers without starting from zero each time.",
    painPoints: [
      "Pre-purchase questions only live in chat, and disappear later.",
      "You cannot tell who is a first-time buyer versus a VIP.",
      "Abandoned interest and past customers get no follow-up.",
    ],
    howSqaleshopHelps: [
      "Let buyers message on WhatsApp before they purchase.",
      "Keep repeat buyers and order history in one customer database.",
      "Use remarketing tools for abandoned orders and repeat outreach on Growth.",
    ],
    outcomes: [
      "Conversations that support sales instead of replacing your store.",
      "Faster recognition of loyal customers.",
      "More recoveries from interest that would have gone cold.",
    ],
    features: [
      {
        title: "WhatsApp integration",
        description: "Let buyers talk before they purchase",
      },
      {
        title: "Customer database",
        description: "Repeat buyers and order history at a glance",
      },
      {
        title: "Remarketing tools",
        description: "Abandoned orders and repeat customer outreach",
      },
    ],
    image: productConnectImg,
  },
}

export function isProductSlug(s: string): s is ProductSlug {
  return (PRODUCT_SLUGS as readonly string[]).includes(s)
}
